/* Import components */

import React, {useEffect, useRef, useState, useCallback, useContext, useMemo} from 'react';
import ReactFlow, {MarkerType, applyNodeChanges, applyEdgeChanges, Controls, Background, NodeToolbar, isEdge} from 'reactflow';
import 'reactflow/dist/style.css';
import {Sidebar} from '../components/ChatbotBuilder';
import {axios} from '../libs';
import {buildWidgetEmbedSnippet, fetchWidgetAssetVersion} from '../libs/widgetEmbed';
import {getChatbotPreviewBaseUrl} from '../libs/paymentPortal';
import {Chatbot} from '../states/actions';
import {useParams} from 'react-router';
import {useSelector} from 'react-redux';
import {nodeTypes} from '../components/ChatbotBuilder/nodeTypes';
import {Form} from '../components';
import {Field} from 'formik';
import {find as _find, filter as _filter} from 'lodash';
import {BlockEditor} from '../components/ChatbotBuilder';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import ClassicEditor from 'ckeditor5-custom-build';
import Chat from '../components/ChatbotBuilder/ChatWidget/Chat';
import {User} from '../states/actions';
import logger from '../utils/logger';
import {remapFlowCanvasObject, remapStartBlockId} from '../helpers/flowBlockIdRemap';
import {ClaudeConnectorModal} from '../components/ChatbotBuilder/ClaudeConnectorModal';
import {getProjectSocket} from '../libs/socket';

/* USER-APP specific */

import 'react-datepicker/dist/react-datepicker.css';
import { GlobalStateContext } from '../components/GlobalState';
//import validationError from '../../../server/helpers/validation-error';

//const ch = await Chatbot.get();
//const {name, blocks_init} = ch;

export const onDelete = async (id) => {
	Chatbot.removeBlock(id, true);
}

const CustomConfirmationDialog = ({ isOpen, message, onConfirm, onCancel, type }) => {
	if (!isOpen) return null;
	return (
	<>
		<div 
		className={`confirmation-dialog ${type}`}
		onClick={onCancel}
		>
			<p>{message}</p>
			{type === "confirm" && (
			<>
			<button onClick={onConfirm}>Yes</button>
			<button className="strong" onClick={onCancel}>No</button>
			</>
			)}
		</div>
		<div className={`confirmation-dialog-bg`} onClick={onCancel}></div>
	</>
	);
};

/* Custom hook to handle the confirmation dialog */

const useCustomConfirm = () => {
	const [isOpen, setIsOpen]   = useState(false);
	const [message, setMessage] = useState('');
	const [resolve, setResolve] = useState(null);
	const confirm = (msg) => {
		setMessage(msg);
		setIsOpen(true);
		return new Promise((_resolve) => {
			setResolve(() => _resolve);
		});
	};
	const handleConfirm = () => {
		setIsOpen(false);
		resolve(true);
	};
	const handleCancel = () => {
		setIsOpen(false);
		resolve(false);
	};
	return { CustomConfirmation: () => 
	<CustomConfirmationDialog
	isOpen    = {isOpen}
	message   = {message}
	onConfirm = {handleConfirm}
	onCancel  = {handleCancel}
	type      = 'confirm'
	/>,
	confirm };
};

const decorateFlowEdge = (edge) => ({
	...edge,
	selectable: false,
	deletable: false,
});

/* Export builder */

export const ChatbotBuilder = React.memo(() => {
	const { CustomConfirmation, confirm }           = useCustomConfirm();
	const { globalState, setGlobalState }           = useContext(GlobalStateContext);
	const reactFlowWrapper                          = useRef(null);
	const importingRef                              = useRef(false);
	const [reactFlowInstance, setReactFlowInstance] = useState(null);
	const graphStyles                               = useMemo(() => ({ width: "100%", height: "90vh" }), []);
	const [loaded, setLoaded]                       = useState(false);
	const [sidebarstate, setSidebarstate]           = useState(false);
	const [editorstate, setEditorstate]             = useState(false);
	const [editelement, setEditElement]             = useState();
	const [isedited, setEdited]                     = useState(false);
	const [isnode, setNode]                         = useState();
	const [isnew, setNew]                           = useState(false);
	const [nodedata, setNodeData]                   = useState();
	const [selectedNodes, setSelectedNodes]         = useState([]);
	const [claudeModalOpen, setClaudeModalOpen]     = useState(false);
	const [claudeReloadBanner, setClaudeReloadBanner] = useState(false);
	const chatbot                                   = useSelector(Chatbot.get());
	
	let state = "";
	
	switch (globalState.preview) {
		case undefined:
		state = true;
		setGlobalState((prev) => ({
			...prev,
			preview: true,
			previewLanguage: prev.previewLanguage ?? 'default',
		}));
		break;
		case true:
		state = true;
		break;
		case false:
		state = false;
		break;
		default:
		state = false;
		break;
	}
	
	const [preview, setPreviewMode] = useState(state);
	const {chatbotId}               = useParams();
	const [widgetAssetVersion, setWidgetAssetVersion] = useState(process.env.REACT_APP_WIDGET_ASSET_VERSION || '');
	const {name, blocks, gtmContainerId, start} = chatbot;
	useEffect(() => {
		let cancelled = false;
		fetchWidgetAssetVersion().then((v) => {
			if (!cancelled && v) setWidgetAssetVersion(v);
		});
		return () => {
			cancelled = true;
		};
	}, []);
	const [nodes, setNodes]         = useState();
	const [edges, setEdges]         = useState();
	const edgesRef                  = useRef([]);
	const blocksRef                 = useRef(blocks);
	edgesRef.current                = edges ?? [];
	blocksRef.current               = blocks;
	/*
	const onNodesChange = useCallback((changes) => setNodes((ns) => applyNodeChanges(changes, ns)),[]); 
	const onEdgesChange = useCallback((changes) => setEdges((es) => applyEdgeChanges(changes, es)),[]);
	*/
	const [tempNodeChanges, setTempNodeChanges] = useState([]);
	const [tempEdgeChanges, setTempEdgeChanges] = useState([]);
	const onNodesChange                         = useCallback(async(changes) => {
		const deleteNodes = changes.filter(change => change.type === 'remove');
		if (deleteNodes.length > 0) {
			setTempNodeChanges(changes);
			deleteNodes.forEach((change) => {
				const el = document.getElementById(change.id);
				if (el) el.classList.add('remove');
			});
			const n = deleteNodes.length;
			const confirmMessage =
				n === 1
					? 'Are you sure you want to delete this block?'
					: `Are you sure you want to delete ${n} blocks?`;
			const confirmDelete = await confirm(confirmMessage);
			if (!confirmDelete) {
				deleteNodes.forEach((change) => {
					const el = document.getElementById(change.id);
					if (el) el.classList.remove('remove');
				});
				setTempNodeChanges([]);
				setTempEdgeChanges([]);
				return;
			}
			setEditElement();
			setEditorstate(false);
			const deletedIds = new Set(deleteNodes.map((change) => change.id));
			const connectedEdges = (edgesRef.current || []).filter(
				(edge) => deletedIds.has(edge.source) || deletedIds.has(edge.target),
			);
			const connectedEdgeIds = new Set(connectedEdges.map((edge) => edge.id));
			for (const edge of connectedEdges) {
				if (!deletedIds.has(edge.source)) {
					await clearSourceConnection(edge);
				}
				Chatbot.removeBlock(edge.id, true);
			}
			deleteNodes.forEach((change) => {
				Chatbot.removeBlock(change.id, true);
			});
			setEdges((es) => (es || []).filter((edge) => !connectedEdgeIds.has(edge.id)));
			setTempNodeChanges([]);
			setTempEdgeChanges([]);
		}
		setNodes((ns) => applyNodeChanges(changes, ns));
	}, [] //[tempNodeChanges, tempEdgeChanges, nodeDelete]
	);
	const onEdgesChange = useCallback(
		(changes) => {
			const isEdgeRemoved = changes.some(change => change.type === 'remove');
			if (isEdgeRemoved) {
				/* Store edge changes temporarily */
				setTempEdgeChanges(changes);
			} else {
				/* Apply edge changes immediately if they are not related to edge removal */
				setEdges((es) => applyEdgeChanges(changes, es));
			}
		},
		[]
	); 
	
	/* This is used to delete edges when a node is deleted, worked only when did not use asyncronous function. */
	
	useEffect(() => {
		logger.log("called")
		if(tempEdgeChanges.length > 0 && tempNodeChanges.length === 0){
			logger.log(tempEdgeChanges)
			setEdges((es) => applyEdgeChanges(tempEdgeChanges, es))
			setTempEdgeChanges([]);
		}
	}, [tempEdgeChanges]);
	
	const normalizeSourceHandle = (handle) => handle ?? 'next';
	const isSameSourceHandle = (edge, source, sourceHandle) =>
		edge.source === source
		&& normalizeSourceHandle(edge.sourceHandle) === normalizeSourceHandle(sourceHandle);
	
	const clearSourceConnection = async (edge) => {
		const sourceBlock = blocksRef.current[edge.source];
		if (!sourceBlock?.data) return;

		const blockType = sourceBlock.data.type;
		const sourceHandle = edge.sourceHandle;
		const sourceId = edge.source;

		if (blockType === 'match' || blockType === 'matchV2') {
			if (sourceHandle === 'false') {
				await Chatbot.setBlockData(sourceId, {nextOnFalse: ''}, true);
			} else {
				await Chatbot.setBlockData(sourceId, {[sourceHandle]: ''}, true);
			}
		} else if (blockType === 'invoice' || blockType === 'project') {
			await Chatbot.setBlockData(sourceId, {[sourceHandle]: ''}, true);
		} else if (
			blockType === 'condition'
			|| blockType === 'payment'
			|| blockType === 'subscription'
			|| blockType === 'subscriptioncheck'
		) {
			await Chatbot.setBlockData(
				sourceId,
				{[sourceHandle === 'true' ? 'nextOnTrue' : 'nextOnFalse']: ''},
				true,
			);
		} else if (blockType === 'decision') {
			if (sourceHandle === 'next') {
				await Chatbot.setBlockData(sourceId, {next: ''}, true);
			} else {
				const decisions = Object.entries(sourceBlock.data.decisions || {})
					.sort(([a], [b]) => a.localeCompare(b))
					.reduce((acc, [key, value]) => ({...acc, [key]: value}), {});
				const decision = decisions[sourceHandle];
				if (decision) {
					await Chatbot.setBlockData(
						sourceId,
						{
							decisions: {
								...decisions,
								[sourceHandle]: {...decision, next: null},
							},
						},
						true,
					);
				}
			}
		} else if (blockType === 'multipleChoice') {
			if (sourceHandle === 'next') {
				await Chatbot.setBlockData(sourceId, {next: ''}, true);
			} else {
				const _options = sourceBlock.data.options;
				const _option = _find(_options, {id: sourceHandle});
				if (_option) {
					await Chatbot.setBlockData(
						sourceId,
						{
							options: {..._options, [_option.id]: {..._option, next: null}},
						},
						true,
					);
				}
			}
		} else if (blockType === 'ai' || blockType === 'router') {
			if (sourceHandle === 'next') {
				await Chatbot.setBlockData(sourceId, {next: ''}, true);
			} else if (sourceHandle === 'nextOnFallback') {
				await Chatbot.setBlockData(sourceId, {nextOnFallback: ''}, true);
			} else {
				const _routes = sourceBlock.data.routes || {};
				const _route = _routes[sourceHandle];
				if (_route) {
					await Chatbot.setBlockData(
						sourceId,
						{
							routes: {..._routes, [_route.id]: {..._route, next: null}},
						},
						true,
					);
				}
			}
		} else if (
			blockType === 'input'
			|| blockType === 'share'
			|| blockType === 'upload'
			|| blockType === 'inputupload'
		) {
			await Chatbot.setBlockData(sourceId, {[sourceHandle]: ''}, true);
		} else {
			await Chatbot.setBlockData(sourceId, {next: ''}, true);
		}
	};

	const deleteEdges = async (edgesToRemove) => {
		if (!edgesToRemove?.length) return;
		logger.log(edgesToRemove.length)
		for (let i = 0; i < edgesToRemove.length; i++) {
			const edge = edgesToRemove[i];
			logger.log("ID " + edge.id)
			Chatbot.removeBlock(edge.id, true);
			await clearSourceConnection(edge);
		}
	}
	
	const storeEdges = (params) => {
		logger.log("Deleting items: ", params.length, params)
		deleteEdges(params);
	};
	
	const nodeDelete = (blocksToDelete) => {
	logger.log(blocksToDelete)
		logger.log("deleting nodes" + blocksToDelete[0].id);
		setEditElement();
		setEditorstate(false)
		Chatbot.removeBlock(blocksToDelete[0].id, true);
	}
	
	const oldWarn = logger.warn;
	
	logger.warn = (warning) => {
		if (warning.includes(`Couldn't create edge for`)) {
			const handleId = warning.split('edge id:').pop();
			logger.log(`Removing un-created handles`, handleId.trim());
			const removeId = handleId.slice(0, handleId.indexOf('Help:')).replace(/\./g, '').replace(/\s/g, '');
			logger.log(removeId)
			Chatbot.removeBlock(removeId, true);
		} else {
			oldWarn(warning);
		}
	};

	/* logger.warn = (warning) => {
		if (warning.includes(`couldn't create edge for`)) {
			const handleId = warning.split(':').pop();
			logger.log(`Removing un-created handles`, handleId.trim());
			Chatbot.removeBlock(handleId.trim(), true);
		} else {
			oldWarn(warning);
		}
	}; */
	
	useEffect(() => { setNode(isnode) }, [isnode]);
	useEffect(() => { 
		// logger.log(selectedNodes)
		window.blockstocopy = selectedNodes
	}, [selectedNodes]);
	
	useEffect(() => {
		logger.log(window.appid)
		window.appid = chatbotId;
		window.appurl = getChatbotPreviewBaseUrl();
		setGlobalState({...globalState, appid: chatbotId})
	}, [window.appid]);
	
	useEffect(() => {
		window.collapseSidebar();
		Chatbot.fetch(chatbotId).then((chatbot) => {
			logger.log("Load and set CHATBOT")
			Chatbot.set(chatbot);
			window.appid = "Test";
		})
		/* Get chatbot blocks */
	}, [chatbotId]);

	const reloadBotFromServer = useCallback(async () => {
		setClaudeReloadBanner(false);
		setLoaded(false);
		setNodes(undefined);
		setEdges(undefined);
		const fresh = await Chatbot.fetch(chatbotId);
		if (fresh) {
			Chatbot.set(fresh);
		}
	}, [chatbotId]);

	useEffect(() => {
		const socket = getProjectSocket();
		socket.connect();
		socket.emit('join_chatbot', chatbotId);
		const onUpdated = () => setClaudeReloadBanner(true);
		socket.on('chatbot_updated', onUpdated);
		return () => {
			socket.off('chatbot_updated', onUpdated);
			socket.emit('leave_chatbot', chatbotId);
		};
	}, [chatbotId]);
	
	useEffect(() => {
		logger.log("Handle input change")
		if(!window.validationError) return null;
		validateInputs(window.validate)
	}, [window.handleInput]);
	
	useEffect(() => {
		logger.log(window.isEdited)
		if(window.isEdited === undefined) return null;
		setEdited(window.isEdited)
	}, [window.isEdited]);
	
	/* Load initial nodes and edges from block data, and do it only once. */
	
	useEffect(() => {
		if(loaded) return
		if(importingRef.current) return
		if(Object.values(blocks).length > 0){
			logger.log(Object.values(blocks).length)
			logger.log("Loaded")
			
			/* Use this to clean up old blocks
			const del = Object.values(blocks).filter(data => data.type === 'step' || data.type === 'smoothstep')
			logger.log(del.length)
			for (let i = 0; i < del.length; i++) {
				logger.log(del[i].id)
				Chatbot.removeBlock(del[i].id, true);
			}
			*/
			
			const flowNodes = Object.values(blocks)
				.filter(data => data.type !== 'step' && data.type !== 'smoothstep');
			const nodeIds = new Set(flowNodes.map((block) => block.id));
			const flowEdges = Object.values(blocks)
				.filter(data => data.type === 'step' || data.type === 'smoothstep')
				.filter((edge) => {
					if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
						return true;
					}
					Chatbot.removeBlock(edge.id, true);
					return false;
				})
				.map(decorateFlowEdge);

			setNodes(
				flowNodes.map(block => ({
					...block,
					position: block.data?.meta?.position || block.position,
				})),
			);
			setEdges(flowEdges);
			setLoaded(true);
			logger.log(Object.values(blocks).length)
			
			//setPreviewMode(chatbot.preview)
		}
	}, [blocks]);

	useEffect(() => {
		if (!loaded || !reactFlowInstance) return;
		const timer = setTimeout(() => {
			reactFlowInstance.fitView({ padding: 0.2 });
		}, 150);
		return () => clearTimeout(timer);
	}, [loaded, reactFlowInstance]);
	
	const handleSelectionChange = (elements) => {
		if (elements.nodes[0] !== undefined) {
			setNode(elements.nodes[0].id)
		}
	};
	
	const [showConfirmation, setShowConfirmation]     = useState(false);
	const [notificationDialog, setNotificationDialog] = useState();
	const NotificationDialog = ({show, onConfirm, onCancel, type, message}) => {
		// if (!show) return null;
		return (
		<div 
		className = {`confirmation-dialog ${type}`}
		onClick   = {onCancel}
		>
			<p>{message}</p>
			{type === "confirm" && (
			<>
				<button onClick={onConfirm}>Yes</button>
				<button className="strong" onClick={onCancel}>No</button>
			</>
			)}
		</div>
		);
	};
	
	/* PREVIOUS DELETE LOGIC *
	const [nodeToDelete, setNodeToDelete] = useState(null);
	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === "Delete") {
				logger.log("NODE delete key")
				if (isnode) {
					//logger.log(isnode, window.el)
					document.getElementById(isnode).classList.add("remove");
					// window.nConfirm({onYes:{handleConfirmDelete}, onNo:{handleCancelDelete}, title: 'Are you sure you want to delete this block?', icon: 'fa-exclamation-triangle'});
					setShowConfirmation(true);
					setNotificationDialog(<NotificationDialog  onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} message="Are you sure you want to delete this block?" type="confirm"/>)
					setNodeToDelete(isnode);
				}
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [nodes]);
	const handleConfirmDelete = () => {
		const newNodes = nodes.filter((node) => node.id !== nodeToDelete);
		setNodes(newNodes);
		setShowConfirmation(false);
		setNodeToDelete(null);
		nodeDelete0(isnode)
	};
	const handleCancelDelete = (nodeid) => {
		setShowConfirmation(false);
		setNodeToDelete(null);
		document.getElementById(isnode).classList.remove("remove");
	};
	const nodeDelete0 = async (id) =>  {
		setEditElement();
		setEditorstate(false)
		Chatbot.removeBlock(id, true);
	}
	*/
	/* Not in use? */
	
	const setPreview = (state) => {
		setPreviewMode(state);
		setGlobalState((prev) => ({
			...prev,
			preview: state,
			previewLanguage: prev.previewLanguage ?? 'default',
		}));
	}

	const setPreviewLanguage = (lang) => {
		setGlobalState((prev) => ({...prev, previewLanguage: lang}));
	};
	
	const onNodeDragStop = (event, node) => {
		
		/* Check if edit mode on */
		
		const editMode = blocks[node.id].editMode;
		Chatbot.setBlockData(
		node.id,
		{
			meta: {
			position: node.position,
			},
		},
		!editMode,
		);
	};
	
	const onConnect = async (params) => {
		logger.log(`file: ChatbotBuilder.js | line 97 | params`, params);
		const blockType = blocks[params.source].data.type;
		const _id = await getBlockId();  
		
		let label = null;
		if (blockType === 'decision' && params.sourceHandle !== "next") {
			label = `${blocks[params.source].data['variable']} : ${
				blocks[params.source].data.decisions[params.sourceHandle].text
			}`;
		} else if ((blockType === 'ai' || blockType === 'router') && params.sourceHandle !== 'next' && params.sourceHandle !== 'nextOnFallback') {
			const route = blocks[params.source].data?.routes?.[params.sourceHandle];
			const routeVar = blocks[params.source].data?.routeVariable || blocks[params.source].data?.variable || '';
			label = route ? `${routeVar} : ${route.value}` : routeVar;
		} else {
			label = blocks[params.source].data['variable'] || '';
		}
		
		const handle = decorateFlowEdge({
			id: _id,
			source: params.source,
			sourceHandle: params.sourceHandle,
			target: params.target,
			targetHandle: params.targetHandle,
			type: 'smoothstep',
			pathOptions: {borderRadius: 30},
			markerEnd: {
				type: MarkerType.ArrowClosed,
				width: 20,
				height: 20,
				color: '#BBC2E7',
			},
			// label,
			data: {},
			style: { width: 100, fontSize: 11, color: 'white', strokeWidth: 3, stroke: '#BBC2E7', },
		});
		setEdges((currentEdges) => {
			const existing = (currentEdges || []).filter((e) =>
				isSameSourceHandle(e, params.source, params.sourceHandle)
			);
			existing.forEach((old) => Chatbot.removeBlock(old.id, true));
			return [
				...(currentEdges || []).filter((e) =>
					!isSameSourceHandle(e, params.source, params.sourceHandle)
				),
				handle,
			];
		});
		
		Chatbot.setBlock(handle);
		Chatbot.setBlockData(
			_id,
			{
				_id,
				type: 'handle',
				chatbotId,
				meta: {handle},
			},
			true,
		);
		/*
		Chatbot.setBlockData(
			params.target,
			{
				previous: params.source,
			},
			true,
		);
		*/


	if (blockType === 'match' | blockType === 'matchV2') {

	  if(params.sourceHandle === 'false'){
		Chatbot.setBlockData(
		  params.source,
		  {
			[params.sourceHandle === 'true' ? 'nextOnTrue' : 'nextOnFalse']: params.target,
		  },
		  true,
		);
	  }else{

		Chatbot.setBlockData(
		  params.source,
		  {
			[params.sourceHandle]: params.target,
		  },
		  true,
		);
	  } 
	}else if (blockType === 'invoice') {
	  // Invoice block uses nextOnSuccess and nextOnFail
	  Chatbot.setBlockData(
		params.source,
		{
		  [params.sourceHandle]: params.target,
		},
		true,
	  );
	}else if (blockType === 'project') {
	  Chatbot.setBlockData(
		params.source,
		{
		  [params.sourceHandle]: params.target,
		},
		true,
	  );
	}else if (blockType === 'condition' || blockType === 'payment' || blockType === 'subscription' || blockType === 'subscriptioncheck') {
	  Chatbot.setBlockData(
		params.source,
		{
		  [params.sourceHandle === 'true' ? 'nextOnTrue' : 'nextOnFalse']: params.target,
		},
		true,
	  );
	} else if (blockType === 'decision') {


  /*     Chatbot.setBlockData(
		params.source,
		{
		  decisions: {
			[params.sourceHandle]: {next: params.target},
		  },
		},
		true,
	  );  */
	  logger.log("OARA:" , params.sourceHandle)

	  if (params.sourceHandle === 'next') {
		logger.log("SIIIIIN");
		Chatbot.setBlockData(
		  params.source,
		  {
			next: params.target,
		  },
		  true,
		);
	  } else {


	  const decisions = Object.entries(blocks[params.source].data.decisions || {})
		  .sort(([a], [b]) => a.localeCompare(b))
		  .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
		  
		Chatbot.setBlockData(
		  params.source,
		  { 
			 decisions: {
			   ...decisions, [params.sourceHandle]: { next: params.target }
			 } 
		  } ,
		  true
		);
	  }

	} else if (blockType === 'multipleChoice') {
	  if (params.sourceHandle === 'next') {
		Chatbot.setBlockData(
		  params.source,
		  {
			next: params.target,
		  },
		  true,
		);
	  } else {
		const _options = blocks[params.source].data.options;
		const _option = _find(_options, {id: params.sourceHandle});
		Chatbot.setBlockData(
		  params.source,
		  {
			options: {..._options, [_option.id]: {..._option, next: params.target}},
		  },
		  true,
		);
	  }
	} else if (blockType === 'ai' || blockType === 'router') {
	  if (params.sourceHandle === 'next') {
		Chatbot.setBlockData(
		  params.source,
		  {next: params.target},
		  true,
		);
	  } else if (params.sourceHandle === 'nextOnFallback') {
		Chatbot.setBlockData(
		  params.source,
		  {nextOnFallback: params.target},
		  true,
		);
	  } else {
		const _routes = blocks[params.source].data.routes || {};
		const _route = _routes[params.sourceHandle];
		if (_route) {
		  Chatbot.setBlockData(
			params.source,
			{
			  routes: {..._routes, [_route.id]: {..._route, next: params.target}},
			},
			true,
		  );
		}
	  }
	} else if (blockType === 'input' || blockType === 'share' || blockType === 'upload' || blockType === 'inputupload') {
	  Chatbot.setBlockData(
		params.source,
		{
		  [params.sourceHandle]: params.target,
		},
		true,
	  );
	} else {
	  Chatbot.setBlockData(
		params.source,
		{
		  next: params.target,
		},
		true,
	  );
	}
  };

  const onDragOver = (event) => {
	event.preventDefault();
	event.dataTransfer.dropEffect = 'move';
  };

  function validateInputs(el) {
	// Remove the `invalid` class from all elements in the document
	document.querySelectorAll('.invalid').forEach((element) => {
	  element.classList.remove('invalid');
	});
  
	// Ensure `el` is an array and not empty
	if (!Array.isArray(el) || el.length === 0) {
	  logger.warn('No invalid elements provided.');
	  return;
	}
  
	logger.log('Invalid IDs to validate:', el);
  
	let firstInvalidElement = null;
  
	// Validate each element in the provided array
	el.forEach((key) => {
	  const inputElement = document.getElementById(key);
  
	  if (inputElement) {
		// Check if input element has a value property and is a string
		if (typeof inputElement.value === 'string' && inputElement.value.trim() !== "") {
		  // Input contains information (is valid)
		  inputElement.classList.add("valid");
		  inputElement.classList.remove("invalid");
		} else {
		  // Input is empty or contains only whitespace (is not valid)
		  inputElement.classList.add("invalid");
		  inputElement.classList.remove("valid");
  
		  // Keep track of the first invalid input to scroll to it
		  if (!firstInvalidElement) {
			firstInvalidElement = inputElement;
		  }
		}
	  } else {
		logger.warn(`Element with ID "${key}" not found in the DOM.`);
	  }
	});
  
	// Scroll to the first invalid element if it exists
	if (firstInvalidElement) {
	  logger.log(`Scrolling to first invalid element: ${firstInvalidElement.id}`);
	  firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
	} else {
	  logger.log('No invalid input elements found to scroll to.');
	}
  }
  
  
  

/*   const onEdgeClick = async (event, node) =>  {
	logger.log(node)
  } */


  const onDrop = async (e) => {
	/* TODO
	Check if there are other new blocks being added, if not saved yet (validation issues) then dont continue. window method is not good because...
	 */
	logger.log('onDrop')
	if(editorstate) {
	  if (typeof window.flushVisualHtml === 'function') {
	    window.flushVisualHtml();
	  }
	  if (isedited || isnew || window.isEdited) {
	    saveBlock();
	  }
	}
	setNew(true) 
	setEditorstate(false)

	try {
	  e.preventDefault();
	  const {type, blockType} = JSON.parse(e.dataTransfer.getData('blockType'));
	  const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
	  const position = reactFlowInstance.project({
		x: e.clientX - reactFlowBounds.left,
		y: e.clientY - reactFlowBounds.top,
	  });
	  const _id = await getBlockId();

	  let addelem = "";
	  logger.log(blockType);
	  if(blockType === 'textBlock'){
		logger.log('textBlock1111')
		addelem = {
		  id: _id,
		  type: blockType,
		  position,
		  editMode: true,
		  button: true,
		  data: {
			_id,
			type,
			chatbotId,
			meta: {
			  position,
			},
		  },
		  //style: { width: 100, fontSize: 11, color: 'white' },
		}
	  }else{
	  addelem = {
		id: _id,
		type: blockType,
		position,
		editMode: true,
		data: {
		  _id,
		  type,
		  chatbotId,
		  meta: {
			position,
		  },
		},
		//style: { width: 100, fontSize: 11, color: 'white' },
	  }
	  }

	  Chatbot.setBlock(addelem);
	  logger.log("SEEEEE", addelem)
	  logger.log(Object.values(blocks).length )
	  if(Object.values(blocks).length === 0){
		setNodes(addelem)
	  } else {
		setNodes(nodes => [...nodes, addelem])
	  }
	 

	  setNode(_id)
	  setEditorstate(true)
	  setEditElement(<BlockEditor node={_id}/>)

	} catch (error) {
	  logger.error(error);
	}
  };



  const createCopy = async (event, node) =>{
 
	event.preventDefault();

	
	logger.log(node)
	const _id = await getBlockId();
	const thisblockdata = await Chatbot.getBlock(node.id);
	logger.log(thisblockdata)
	const newobject = deepClone(thisblockdata);
	newobject["position"]["x"] += 200
	newobject["data"]["_id"] = _id
	newobject.editMode = true
	newobject.id = _id
	if(newobject.data && newobject.data.variable){
	newobject.data.variable = newobject.data.variable + "_copy"
	}

	logger.log(newobject)
	Chatbot.setBlock(newobject);

	setNew(true) 

	logger.log(Object.values(blocks).length )
	if(Object.values(blocks).length === 0){
	  setNodes(newobject)
	} else {
	  setNodes(nodes => [...nodes, newobject])
	}

	
  /*   const response = await Chatbot.setBlockData(newobject.id, {}, true);
	logger.log(response) */
   


	setTimeout(() => {
	  setNode(_id) 
	  setEditorstate(true)
	  setEditElement(<BlockEditor node={_id}/>)
  
	}, "10")


  /*   let block = "";
	block = _find(chatbot.blocks, {editMode: true}); 

	logger.log(block) */

   /*  const response = await Chatbot.setBlockData(_id, {}, true);
	logger.log(response) */

   // saveBlock();


  }
  function deepClone(obj) {
	return JSON.parse(JSON.stringify(obj));
}



/* Create copies into database */
const createBlocksCopies = async (data) =>{
  
  /* using let handle as obj, because handle will be the object name in meta data of handle obj, otherwise handle as object name is not needed */

  logger.log(data)

  for (let key in data) {
	  let handle = data[key];
	  Chatbot.setBlock(handle);

	  if(handle.data.type === "handle"){
	   const _id = handle.id
	   const response = Chatbot.setBlockData(
		_id,
		{
		  _id: handle.id,
		  type: 'handle',
		  chatbotId: chatbot._id,
		  meta: {handle},
		},
		true,
	  );
	  logger.log(response)
	  setEdges((es) => [...(es || []), decorateFlowEdge(handle)]);
	 } else {
	  const response = await Chatbot.setBlockData(handle.id, {}, true);
	  setNodes((nodes) => [...(nodes || []), handle]);
	  logger.log(response)
	}
	
	 
}
setTimeout(() => {

  reactFlowInstance.fitView()

}, "200")




}
const updateIDsAndReferences = async (data) =>  {
  logger.log(chatbotId)
  let newbotid = chatbotId;
  let idMap = {};

  // Create mapping of old IDs to new IDs
  for (let key in data) {

	  idMap[key] = await getBlockId();
  }

  // Replace object keys and IDs with new IDs
  let updatedData = {};
  for (let key in data) {
	  let newObjKey = idMap[key];
	  updatedData[newObjKey] = { ...data[key], id: newObjKey};
	  // If the data object has an _id field, update that as well
	  if (updatedData[newObjKey].data && updatedData[newObjKey].data._id) {
		  updatedData[newObjKey].data._id = newObjKey;
	  }
	  if (updatedData[newObjKey].data && updatedData[newObjKey].data.chatbotId) {
		updatedData[newObjKey].data.chatbotId = newbotid;
	}
  }

  for (let key in updatedData) {
	  remapFlowCanvasObject(updatedData[key], idMap);
  }

  logger.log(updatedData)
  return {updatedData, idMap};
}

const importNodes = async (nodedata) => {
	 importingRef.current = true;
	 try {
	 logger.log(nodedata)
	 const isFullExport =
	   nodedata &&
	   typeof nodedata === 'object' &&
	   !Array.isArray(nodedata) &&
	   Object.prototype.hasOwnProperty.call(nodedata, 'blocks') &&
	   typeof nodedata.blocks === 'object';
	 const blocksPayload = isFullExport ? nodedata.blocks : nodedata;
	 const importStart = isFullExport ? nodedata.start : undefined;

	 const deepcopy = await deepClone(blocksPayload);
	 const {updatedData, idMap} = await updateIDsAndReferences(deepcopy);
	 await createBlocksCopies(updatedData);

	 const mappedStart = remapStartBlockId(importStart, idMap);
	 if (mappedStart) {
	   await Chatbot.set({start: mappedStart}, true);
	 }
	 setLoaded(true);
	 } finally {
	   importingRef.current = false;
	 }
}



/*IN future if import json data to blocks is needed  */



 const fileInputRef = useRef(null);
 const [jsonData, setJsonData] = useState(null);
 const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (file) {
	  const reader = new FileReader();
	  reader.onload = (e) => {
		  try {
			  const json = JSON.parse(e.target.result);
			  //setJsonData(json);
			  importNodes(json)
			  logger.log(json)
		  } catch (error) {
			  logger.error("Error parsing JSON:", error);
			  alert("Invalid JSON file.");
		  }
	  };
	  reader.readAsText(file);
  }
 };



 useEffect(() => {
	  document.addEventListener('keydown', handleKeyDown);

  return () => {
	  document.removeEventListener('keydown', handleKeyDown);
  };
}, []);

const isFlowCanvasEditableTarget = (target) => {
	if (!target || !(target instanceof Element)) return false;
	if (target.closest('.node-editor')) return true;
	const tag = target.tagName;
	if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
	if (target.isContentEditable) return true;
	if (target.closest('.ck-editor') || target.closest('[contenteditable="true"]')) return true;
	return false;
};

const handleKeyDown = (event) => {
  if (event.ctrlKey || event.metaKey) {
	  switch (event.key.toLowerCase()) {
		  case 'a':
			  if (isFlowCanvasEditableTarget(event.target)) break;
			  event.preventDefault();
			  setNodes((ns) => {
				  if (!ns?.length) return ns;
				  return ns.map((node) => ({ ...node, selected: true }));
			  });
			  break;

		  case 'c':
			  logger.log(window.blockstocopy)
			  window.blockss = window.blockstocopy
			  setShowConfirmation(true);
			  setNotificationDialog(<NotificationDialog show={showConfirmation} message="Copied blocks" type="noti"/>)
			  break;

		  case 'v':
			  logger.log(window.blockss)
			  importNodes(window.blockss)
			  break;
		  default:
			  break;
	  }
  }
};



 // const isNodeSelected = (id) => selectedNodes.some(node => node.id === id);

  const onClick = async (event, node) => {

	/* Group select if ctrl down. Still some issus when selecting and deselecting. */
	if (event.ctrlKey || event.metaKey) {


   //   if (!isNodeSelected(node.id)) {

		logger.log("not")
		const nodedata = _find(chatbot.blocks, {id: node.id}); 
		logger.log(nodedata)
		let deepclone = {[nodedata.id]: nodedata}
	  // const nodedata = await Chatbot.getBlock(node.id);
	  // const deepclone = deepClone(nodedata);
	   //const deepclone2 = {[deepclone.id]: deepclone}

		logger.log(deepclone)

		  setSelectedNodes(prevSelected => ({...prevSelected, ...deepclone}));          
		  document.getElementById(node.id).classList.add("nodeaction");

	 // } else {
		//  setSelectedNodes((prevSelected) => prevSelected.filter(node => node.id !== node.id));
		//  document.getElementById(node.id).classList.remove("nodeaction");
	 // }


	  setTimeout(() => {

		logger.log(selectedNodes)
		
	  }, "100")
	  
	} else {
	
  


	setShowConfirmation(false);
   
	//if(nodeToDelete !== null) handleCancelDelete()

	/* Handle Save. Save only if there have been changes. For better performance.
	This is controlled using window object and defining window.edited in onchange event inside input or other component. */
	if (typeof window.flushVisualHtml === 'function') {
	  window.flushVisualHtml();
	}
	if(isedited || isnew || window.isEdited){
	  const save = await saveBlock(node); 
	  if(save.error) return null;
	}
	
	/* This is to prevent onClick event happening again.
	This can be changed to use local useState manager for active node. */
	if(window.el !== node.id) return null;
	window.el = "";


	setEditElement();

	// Setting clicked block editmode true:
	//Chatbot.setBlock({id: node.id, editMode: true});
  

	setTimeout(() => {

	  /* Can I remove setnode, is setEditelemet and editorstate both needed? */

	  setNode(node.id) 
	  setEditorstate(true)
	  setEditElement(<BlockEditor node={node.id}/>)
	  window.isEdited = false;
	  setEdited(false)
  
	}, "10")



  }
   
  };

  function downloadJSON(data, name) {
	// Prompt for filename
	logger.log(name)
	let filename = "";
	if(name){
	   filename = name;
	}else{
	  filename = prompt("Enter filename:", "data.json");
	}
	if (!filename) return;  // If user cancels the prompt, exit the function

	// Convert the data to a JSON string
	const jsonString = JSON.stringify(data, null, 2);  // 2 spaces for indentation

	// Create a Blob from the JSON string
	const blob = new Blob([jsonString], { type: 'application/json' });

	// Create a link element and trigger the download
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = filename.endsWith('.json') ? filename : `${filename}.json`;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

  function clearnodeaction () {

	// Get all elements with the class name 'your-classname'
	
	const node = document.getElementsByClassName('node');
	logger.log(node.length)
	   // Loop through each element and remove the class 'class-to-remove'
	   for (let i = 0; i < node.length; i++) {
		node[i].classList.remove('nodeaction');
	}
	

 
  }


  const onPaneClick = async (node) => {
	
	/* clear nodeaction */
	if(Object.keys(selectedNodes).length > 0){
	clearnodeaction();
	}
	setSelectedNodes([]);






	setShowConfirmation(false);
 
	//if(nodeToDelete !== null) handleCancelDelete()
	
	window.progress = false;

	/* logger.log(isedited, isnew) */
	if (typeof window.flushVisualHtml === 'function') {
	  window.flushVisualHtml();
	}
	if(isedited || isnew || window.isEdited){
	  const save = await saveBlock(); 
	  if(save.error) return null;
	}

	setEditorstate(false);

  };



const saveBlock = async () =>  {

	

	let block = "";
	if(isnew){
	  logger.log("new")
	   block = _find(chatbot.blocks, {editMode: true});
	}else{
	  logger.log("old")
	   block = _find(chatbot.blocks, {id: isnode})
	}
	logger.log("SAVING", block)


	if (block) {


	  /* When delete second route from editor - delete edge that is connected to it */
	  if((block.type === 'inputBlock' || block.type === 'inputuploadBlock') && block.data.secondbutton === "" && block.data.next_secondary !== ""){

		/* should update node edges but something is wrong. FIX */
		const newEdges = edges.filter((edge) => edge.sourceHandle !== 'next_secondary' && edge.source !== block.data._id);
		const edgeToDelete = edges.filter((edge) => edge.sourceHandle === 'next_secondary' && edge.source === block.data._id);
		Chatbot.setBlockData(block.id, {next_secondary : ""})
		Chatbot.removeBlock(edgeToDelete[0].id, true);
	  }
	  
	  logger.log("SAV", block.id)
	  const response = await Chatbot.setBlockData(block.id, {}, true);
	  logger.log(response)
	  if(response.type === 'input'){logger.log('INPUT BLOCK')}
	  logger.log(response)


	  if (!response.error) {
	   /*  if(response.error){
		  let {error, message} = response;
		  const tovalidate = Object.keys(error);
		  window.validate = tovalidate;
		  window.validationError = true;
		  
		  validateInputs(tovalidate)
		  setShowConfirmation(true);
		  setNotificationDialog(<NotificationDialog show={showConfirmation} message="Fill out the required fields" type="error"/>)
		  setNew(false)

		  return {
			error: true,
		  }
		} */
	   /* NO ERROR */
		Chatbot.setBlock({id: block.id, editMode: false});
		setNew(false)
		//setEdited(false)
		return {
		  error: false,
		}
	  } else {

		let {error, message} = response;
		const tovalidate = Object.keys(error);
		logger.log("KEYS TO VALIDATE:" + tovalidate)
		window.validate = tovalidate;
		window.validationError = true;
		
		validateInputs(tovalidate)
		
		setShowConfirmation(true);
		setNotificationDialog(<NotificationDialog show={showConfirmation} message="Fill out the required fields" type="error"/>)
		setNew(true)
		//window.nError({title: 'Need to fill out required fields', text: message, icon: 'fa-exclamation-triangle', error});

		return {
		  error: true,
		}
	  }
	}else{
	  return {
		error: false,
	  }
	}
  }


  const getBlockId = async () => {
	let {id} = await axios.get('/block/id');
	//error checking
	return id;
  };

  const initFlow = async (instance) => {
	setReactFlowInstance(instance)
  }
/*   const customKeyHandler = (event) => {
	if (event.code === 'Backspace') {
	  // Prevent backspace and delete key events
	  event.preventDefault();
	  return false;
	}
  
	return true;
  }; */
	return (
	<>
		<ClaudeConnectorModal
			isOpen={claudeModalOpen}
			onClose={() => setClaudeModalOpen(false)}
			chatbotId={chatbotId}
			botName={name}
		/>
		{claudeReloadBanner && (
			<div
				className="alert alert-warning mb-0 rounded-0 text-center"
				role="alert"
				style={{cursor: 'pointer'}}
				onClick={reloadBotFromServer}
			>
				Bot was changed via Claude connector — click to reload
			</div>
		)}
		<div className="x_panel chatbot-builder">
			<div className="header-menu d-flex justify-content-between align-items-center">
				<div className="header-actions d-flex align-items-center">
					<div className="action action-add" onClick={()=> {setSidebarstate(sidebarstate ? false : true, sidebarstate ? true : false)}}>
						<a class="button" title="Add element">
							{sidebarstate ? '-' : '+'}
						</a> 
					</div>
					<div className="action action-switch" onClick={() => { setPreview(preview ? false : true)}}>
						<div className={`switch ${preview ? 'on' : 'off'}`} title={`${preview ? 'Turn preview OFF' : 'Turn preview ON'}`}>
							<span></span>
						</div>
					</div>
					{preview && (
						<div className="action action-lang d-flex align-items-center" title="Preview language">
							<i
								className={`mx-1 btn-circle ${(globalState.previewLanguage ?? 'default') === 'default' ? 'active' : ''}`}
								onClick={() => setPreviewLanguage('default')}
							>
								{' '}et{' '}
							</i>
							<i
								className={`mx-1 btn-circle ${globalState.previewLanguage === 'en' ? 'active' : ''}`}
								onClick={() => setPreviewLanguage('en')}
							>
								{' '}en{' '}
							</i>
							<i
								className={`mx-1 btn-circle ${globalState.previewLanguage === 'ru' ? 'active' : ''}`}
								onClick={() => setPreviewLanguage('ru')}
							>
								{' '}ru{' '}
							</i>
						</div>
					)}
				</div>
				<div className="header-title d-flex align-items-center flex-wrap gap-2">
					<h1 className="mb-0">{name}</h1>
					<label className="d-flex align-items-center gap-1 mb-0" title="Google Tag Manager container ID for this flow">
						<span className="text-muted small">GTM:</span>
						<input
							type="text"
							className="form-control form-control-sm"
							style={{width: '140px'}}
							placeholder="GTM-XXXXXXX"
							defaultValue={gtmContainerId || ''}
							onBlur={(e) => {
								const value = e.target.value.trim();
								Chatbot.set({gtmContainerId: value}, true);
							}}
						/>
					</label>
				</div>
				<div className="header-actions d-flex align-items-center">
					<div className="action action-variables">
						<a onClick={() => {window.open(`/chatbot-variables/${chatbotId}`, '', `width=600,height=${window.screen.height}`)}} title="Data">
							<i class="fa-solid fa-database"></i>
						</a>
					</div>
					<div className="action action-upload">
						<input 
						type     = "file" 
						ref      = {fileInputRef} 
						onChange = {handleFileChange} 
						accept   = ".json"
						style    = {{ display: 'none' }}
						/>
						<a onClick={() => fileInputRef.current.click()} title="Upload JSON">
							<i className='fa-solid fa-upload'></i>
						</a>
					</div>
					<div className="action action-download">
						<a onClick={() => {downloadJSON({blocks, start}, name)}} title="Download JSON">
							<i className='fa-solid fa-download'></i>
						</a>
						{Object.keys(selectedNodes).length > 0 && (
							<a onClick={() => {downloadJSON(selectedNodes)}} title="Download JSON">
								<i className='fa-solid fa-download'></i>
							</a> 
						)}
					</div>
					{User.isAdmin() && (
					<div className="action action-claude">
						<a onClick={() => setClaudeModalOpen(true)} title="Claude connector">
							<i className="fa-solid fa-plug" aria-hidden="true" />
						</a>
					</div>
					)}
					<div className="action action-preview">
						<a href={`${window.appurl+window.appid}`} target="_blank" title="Preview">
							<i className='fa-solid fa-eye'></i>
						</a>
					</div>
					{/* {User.isAdmin() ? (
					<button className="btn btn-primary btn-sm" data-bs-toggle="modal" data-bs-target="#modalPublish">
						Publish
					</button>
					) : (
					<button className="btn btn-success btn-sm" data-bs-toggle="modal" data-bs-target="#modalSubmit">
						Submit for approval
					</button>
					)}*/}
				</div>
			</div>
			<div className="x_content">
			  {/* <ChatPreview /> */}
			  {/* <Chat /> */}
			  <div className="row h-100">
				
				 {sidebarstate && (
				  <div className="addcontent">
				  <Sidebar />
				  </div>
				  )}
			  
				<div className="col-12 p-0" ref={reactFlowWrapper} >
				  <CustomConfirmation />
				  <span
					className="d-none"
					data-hugo-widget-embed={buildWidgetEmbedSnippet(chatbotId, globalState.previewLanguage || 'en', widgetAssetVersion)}
					aria-hidden="true"
				  />

				  <ReactFlow 
				   // onInit={(instance) => {setReactFlowInstance(instance)}}
				   // onInit={(instance) => setTimeout(() => instance.fitView(), 100)}
					 onInit={initFlow}
					//onload={onLoad}
					//elementsSelectable={true}
				   // keyDown={customKeyHandler}
					
					//disable delete key
					//deleteKeyCode={null}
				   
					nodes = {nodes}
					edges = {edges}
					onNodesChange={onNodesChange}
					onEdgesChange={onEdgesChange}

					onConnect={onConnect}
					onDrop={onDrop}
					onDragOver={onDragOver}
					onNodeDragStop={onNodeDragStop}

					//onNodeDoubleClick={onNodeDoubleClick}   HILJEM
				   
				   // zoomTo(node)
					/* onMove={handleMove} */

					onNodeClick={onClick}
				   // onNodeDoubleClick={onDoubleClick}
				  
				  
				  
				  
				  
					onNodeContextMenu={createCopy}
				  
				  
				  
				   // onEdgeClick={onEdgeClick}
					onEdgesDelete={storeEdges}
					//onNodesDelete={nodeDelete}

					onSelectionChange={handleSelectionChange}

					snapToGrid={true}
					nodeTypes={nodeTypes}

					/* deleteKeyCode={46} */
					onPaneClick={onPaneClick}
					minZoom={0.1}


					style={graphStyles}
					>


					<Controls />
					<Background gap={20} />
				  </ReactFlow>
				</div>

				{showConfirmation && notificationDialog}
			   


				{editorstate && (
				<div className="node-editor">



					{isnode !== undefined && (
					  <>
					  {editelement}
					  </>
					 )}
			  
				</div>
				)}

			  </div>
			</div>
	  </div>
	 

	{/*   <div
		className="modal fade"
		id="modalSubmit"
		data-bs-backdrop="static"
		data-bs-keyboard="false"
		tabIndex="-1"
		aria-labelledby="staticBackdropLabel"
		aria-hidden="true"
	  >
		<div className="modal-dialog">
		  <Form url="/chatbot/submit" initialValues={{chatbotId}}>
			<div className="modal-content">
			  <div className="modal-header">
				<h6 className="modal-title" id="staticBackdropLabel">
				  Submit
				</h6>
				<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
			  </div>
			  <div className="modal-body">
				<div className="mb-3">
				  <label htmlFor="description" className="form-label d-none">
					Description
				  </label>
				  <Field
					as="textarea"
					id="description"
					name="description"
					className="form-control"
					placeholder="This bot deals with all the required employment contracts"
					rows="6"
				  />
				  <div className="form-text">
					Write a small description about your chatbot, (also if you apply for changes then mention what has
					changed)
				  </div>
				</div>
			  </div>
			  <div className="modal-footer">
				<button type="button" className="btn btn-secondary btn-sm" data-bs-dismiss="modal">
				  Close
				</button>
				<button type="submit" className="btn btn-primary btn-sm">
				  Submit
				</button>
			  </div>
			</div>
		  </Form>
		</div>
	  </div> */}




	  {/* PROBLEEM, AGA SEE EI OLE KA KASUTUSEL
	  
		  <div
			className="modal fade"
			id="modalPublish"
			data-bs-backdrop="static"
			data-bs-keyboard="false"
			tabIndex="-1"
			aria-labelledby="staticBackdropLabel"
			aria-hidden="true"
		  >
			<div className="modal-dialog">
			  <div className="modal-content">
				<div className="modal-header">
				  <h6 className="modal-title" id="staticBackdropLabel">
					Publish this Chatbot
				  </h6>
				  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
				</div>
				<div className="modal-body">
				  <div className="mb-3">
					<label htmlFor="description" className="form-label d-none">
					  Chatbot Script Tag
					</label>
					<textarea
					  id="publishCode"
					  name="publishCode"
					  className="form-control"
					  placeholder=""
					  rows="12"
					  value={`<link href="https://dev-admin.hugo.legal/widget/css/chat-widget.css" rel="stylesheet" />


					  <script>
						var chatbotId = '${chatbotId}';
						var lang = 'en';
					  </script>
					  <script src="https://dev-admin.hugo.legal/widget/js/runtime.js"></script>
					  <script src="https://dev-admin.hugo.legal/widget/js/chunk.js"></script>
					  <script src="https://dev-admin.hugo.legal/widget/js/main.js"></script>`}
					/>
					<div className="form-text">Add this code to your website's "head" tag</div>
				  </div>
				</div>
			  </div>
			</div>
		  </div> 
	  */}
	</>
  );
  
});
